import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppMutation, useLocale } from '../../../../hooks/index';
import ApiService from '../../../../services/api-service.util';
import { SigningMode } from '../../templates/enums/template.const';
import {
  getQuickSignStepFourFile,
  getQuickSignStepFourFileLink,
  QuickSignFormSchema,
} from '../validation/quick-sign.validation';

export type QuickSignResponse = {
  signatories: Array<{ id: string }>;
  signature: { id: string };
};

const buildQuickSignBody = (data: QuickSignFormSchema): Record<string, unknown> => {
  const { stepOne, stepTwo, stepThree, stepFour } = data;
  const fileLink = getQuickSignStepFourFileLink(stepFour.file);
  const file = getQuickSignStepFourFile(stepFour.file);
  if (!fileLink) throw new Error('File link is required');

  return {
    name: stepOne.name,
    category: stepOne.category,
    subCategory: stepOne.subCategory,
    ...(stepOne.linkedTargetId ? { linkedTargetId: stepOne.linkedTargetId } : {}),
    fileLink,
    documentName: file?.name ?? stepOne.name,
    ...(stepTwo.expireDocument && stepTwo.expirationPeriod != null
      ? { expirationPeriod: String(stepTwo.expirationPeriod) }
      : {}),
    signingOrderMatters: stepTwo.signingMode === SigningMode.SET_ORDER,
    signatorySlots: stepThree.signatorySlots
      .filter((slot) => slot.stakeholder != null)
      .map((slot) => ({ stakeholderId: slot.stakeholder!.id })),
    tabs: (stepFour.tabs ?? [])
      .filter((tab) => tab.type != null && tab.value != null)
      .map((tab) => ({
        anchorString: tab.anchorString,
        type: tab.type,
        signerStakeholderId: tab.value,
      })),
  };
};

export type QuickSignSubmitOptions = {
  onSuccess?: () => void;
};

export const useQuickSign = () => {
  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();

  const { isPending, mutate: submitQuickSign } = useAppMutation<
    QuickSignResponse,
    Error,
    { data: QuickSignFormSchema; options?: QuickSignSubmitOptions }
  >([QueryKey.QUICK_SIGN], {
    mutationFn: ({ data }) => {
      const body = buildQuickSignBody(data);
      return ApiService.post<QuickSignResponse>(
        { endpoint: BackendRoute.SIGNATURES_QUICK_SIGN },
        { body },
      );
    },
    onSuccess: (_data, variables) => {
      toast.success(messagesLocale['QUICK_SIGN_SENT_SUCCESSFULLY']);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SIGNATURES] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENTS] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDER] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDERS] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_STAKEHOLDERS] });
      variables.options?.onSuccess?.();
    },
  });

  return { isPending, submitQuickSign };
};
